<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import authService from '../api/auth';
import { FileText, ArrowLeft } from 'lucide-vue-next';

const router = useRouter();

const isLogin = ref(true);
const email = ref('');
const password = ref('');
const firstName = ref('');
const lastName = ref('');
const isLoading = ref(false);
const error = ref('');

const handleSubmit = async () => {
  error.value = '';
  isLoading.value = true;

  try {
    if (isLogin.value) {
      await authService.login(email.value, password.value);
    } else {
      await authService.register(
        email.value,
        password.value,
        firstName.value,
        lastName.value
      );
    }
    
    router.push('/dashboard');
  } catch (err: any) {
    error.value = err.response?.data?.error || 'Wystąpił błąd. Spróbuj ponownie.';
  } finally {
    isLoading.value = false;
  }
};

const toggleMode = () => {
  isLogin.value = !isLogin.value;
  error.value = '';
};

const goHome = () => {
  router.push('/');
};
</script>

<template>
  <div class="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-slate-50">
    <!-- Back Button -->
    <div class="absolute top-6 left-6">
      <button
        @click="goHome"
        class="flex items-center gap-2 text-slate-600 hover:text-indigo-600 font-medium transition"
      >
        <ArrowLeft class="h-5 w-5" />
        Powrót do strony głównej
      </button>
    </div>

    <div class="sm:mx-auto sm:w-full sm:max-w-md text-center">
      <div class="flex justify-center items-center gap-2 mb-6">
        <FileText class="h-10 w-10 text-indigo-600" />
        <span class="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">
          CV-Tailor AI
        </span>
      </div>
      <h2 class="mt-6 text-3xl font-extrabold text-slate-900">
        {{ isLogin ? 'Witaj ponownie' : 'Utwórz swoje konto' }}
      </h2>
      <p class="mt-2 text-sm text-slate-600">
        {{ isLogin ? 'Zaloguj się, aby zarządzać optymalizacją CV' : 'Rozpocznij optymalizację CV dla dowolnej pracy' }}
      </p>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white py-8 px-4 shadow-xl shadow-indigo-100 rounded-2xl sm:px-10 border border-slate-100">
        <!-- Error Alert -->
        <div v-if="error" class="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <p class="text-red-700 text-sm font-medium">{{ error }}</p>
        </div>

        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- Register Fields -->
          <div v-if="!isLogin" class="grid grid-cols-2 gap-4">
            <div>
              <label for="firstName" class="block text-sm font-medium text-slate-700">Imię</label>
              <input
                id="firstName"
                v-model="firstName"
                type="text"
                class="mt-1 appearance-none block w-full px-3 py-3 border border-slate-300 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Jan"
              />
            </div>
            <div>
              <label for="lastName" class="block text-sm font-medium text-slate-700">Nazwisko</label>
              <input
                id="lastName"
                v-model="lastName"
                type="text"
                class="mt-1 appearance-none block w-full px-3 py-3 border border-slate-300 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Kowalski"
              />
            </div>
          </div>

          <!-- Email Field -->
          <div>
            <label for="email" class="block text-sm font-medium text-slate-700">Email</label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              class="mt-1 appearance-none block w-full px-3 py-3 border border-slate-300 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="john@example.com"
            />
          </div>

          <!-- Password Field -->
          <div>
            <label for="password" class="block text-sm font-medium text-slate-700">Hasło</label>
            <input
              id="password"
              v-model="password"
              type="password"
              required
              class="mt-1 appearance-none block w-full px-3 py-3 border border-slate-300 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="••••••••"
            />
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-lg shadow-indigo-500/20 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {{ isLoading ? 'Przetwarzanie...' : (isLogin ? 'Zaloguj się' : 'Utwórz konto') }}
          </button>
        </form>

        <!-- Toggle Mode -->
        <div class="mt-6 text-center">
          <p class="text-sm text-slate-600">
            {{ isLogin ? "Nie masz konta?" : 'Masz już konto?' }}
            <button
              type="button"
              @click="toggleMode"
              class="font-medium text-indigo-600 hover:text-indigo-700"
            >
              {{ isLogin ? 'Zarejestruj się' : 'Zaloguj się' }}
            </button>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
